import {
	AfterViewInit,
	Component,
	ContentChildren,
	ElementRef,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	QueryList,
	Renderer2,
	forwardRef,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatStepper } from '@angular/material/stepper';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { UrlSegment, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import {
	BREADCRUMB_UNIQUE_QUERYPARAM,
	NavigationHistoryService,
} from 'src/app/services/navigation-history.service';

@Component({
	selector: 'app-tab-navigation-header',
	template: `
		<div class="navigation-header-container">
			<app-breadcrumb></app-breadcrumb>
			<ng-content></ng-content>
		</div>
	`,
	styleUrls: ['app-navigation-header.component.scss'],
	imports: [forwardRef(() => AppBreadcrumbComponent)],
})
export class AppTabNavigationHeaderComponent
	implements AfterViewInit, OnChanges, OnDestroy
{
	@Input() breadcrumb!: Breadcrumb;
	@Input() title = '';
	@Input() detail?: string;

	@ContentChildren(MatTabGroup, { descendants: true, read: ElementRef })
	contentChildren!: QueryList<ElementRef>;

	@ContentChildren(MatTabGroup, { descendants: true })
	tabGroups!: QueryList<MatTabGroup>;

	@ContentChildren(MatTab, { descendants: true, read: ElementRef })
	tabHostElements!: QueryList<ElementRef>;

	private tabsChangeSubscription?: Subscription;

	constructor(private readonly renderer: Renderer2) {}

	get hasDetail(): boolean {
		return !!this.detail;
	}

	ngAfterViewInit(): void {
		this.updateTabHeader();
		this.scheduleAutomationIds();
		this.subscribeToTabChanges();
	}

	ngOnChanges(): void {
		this.updateTabHeader();
		this.scheduleAutomationIds();
	}

	ngOnDestroy(): void {
		this.tabsChangeSubscription?.unsubscribe();
		this.tabsChangeSubscription = undefined;
	}

	private scheduleAutomationIds(): void {
		setTimeout(() => {
			this.addAutomationIdsToTabs();
		}, 300);
	}

	private subscribeToTabChanges(): void {
		const tabGroup = this.tabGroups?.first;
		if (!tabGroup) {
			return;
		}

		this.tabsChangeSubscription?.unsubscribe();
		this.tabsChangeSubscription = tabGroup._tabs.changes.subscribe(() => {
			this.scheduleAutomationIds();
		});
	}

	private formatAutomationIdTabStrings(str: string) {
		// Convert the string to lowercase
		let lowerCaseStr = str.trim().toLowerCase();
		// Split the string by spaces
		let splitStr = lowerCaseStr.split(' ');
		// Join the split string with dashes
		let formattedStr = splitStr.join('-');
		return formattedStr;
	}

	private resolveTabAutomationId(
		tab: MatTab | undefined,
		tabHost: Element | undefined,
		fallbackLabelText: string
	): string {
		const explicitId = tabHost?.getAttribute('data-automation-id');
		if (explicitId) {
			return explicitId;
		}

		if (tab?.ariaLabel) {
			return `${this.formatAutomationIdTabStrings(tab.ariaLabel)}-tab`;
		}

		const ariaLabel = tabHost?.getAttribute('aria-label');
		if (ariaLabel) {
			return `${this.formatAutomationIdTabStrings(ariaLabel)}-tab`;
		}

		return `${this.formatAutomationIdTabStrings(fallbackLabelText)}-tab`;
	}

	private addAutomationIdsToTabs(): void {
		const tabGroupElement = this.contentChildren?.first?.nativeElement;
		const tabGroup = this.tabGroups?.first;
		if (!tabGroupElement || !tabGroup) {
			return;
		}

		const tabHeaders = tabGroupElement.querySelectorAll('.mat-mdc-tab');
		const tabLabelText = tabGroupElement.querySelectorAll(
			'.mdc-tab__text-label'
		);
		const tabs = tabGroup._tabs.toArray();
		const tabHosts = this.tabHostElements?.toArray() ?? [];

		if (
			!tabHeaders?.length ||
			!tabLabelText?.length ||
			tabHeaders.length !== tabLabelText.length ||
			tabs.length !== tabHeaders.length
		) {
			return;
		}

		for (let i = 0; i < tabHeaders.length; i++) {
			const automationId = this.resolveTabAutomationId(
				tabs[i],
				tabHosts[i]?.nativeElement,
				tabLabelText[i].textContent ?? ''
			);
			this.renderer.setAttribute(
				tabHeaders[i],
				'data-automation-id',
				automationId
			);
		}
	}

	private updateTabHeader(): void {
		const matTabContainer =
			this.contentChildren?.first?.nativeElement?.querySelector(
				'.mat-mdc-tab-header'
			);
		if (matTabContainer) {
			let appTabContainer = matTabContainer.closest(
				'.navigation-header-tab-container'
			);
			if (!appTabContainer) {
				appTabContainer = wrapElementWith(
					matTabContainer,
					'div',
					['navigation-header-tab-container'],
					this.renderer
				);
			}

			appTabContainer
				.querySelector('.navigation-header-title-container')
				?.remove();

			const detailHtml = !!this.detail
				? `&nbsp;<span class="navigation-header-detail mat-h1">|&nbsp;${this.detail}</span>`
				: '';
			const titleHtml = `<div class="navigation-header-title-container"><span class="navigation-header-title mat-h1">${this.title}</span>${detailHtml}</div>`;
			appTabContainer.insertAdjacentHTML('afterbegin', titleHtml);
		}
	}
}

@Component({
	selector: 'app-navigation-header',
	template: `
		<div class="navigation-header-container">
			<app-breadcrumb></app-breadcrumb>
			<div class="navigation-header-basic-container">
				<div class="navigation-header-title-container">
					<span
						class="navigation-header-title mat-h1"
						data-automation-id="navigation-header-page-title"
						>{{ title }}</span
					>
					@if (hasDetail) {
						<span class="navigation-header-detail mat-h1"
							>&nbsp;|&nbsp;{{ detail }}</span
						>
					}
				</div>
			</div>
			<ng-content></ng-content>
		</div>
	`,
	styleUrls: ['app-navigation-header.component.scss'],
	imports: [forwardRef(() => AppBreadcrumbComponent)],
})
export class AppNavigationHeaderComponent {
	@HostBinding('class') hostClass = 'app-navigation-header';

	@Input() title = '';
	@Input() detail?: string;

	constructor() {}

	get hasDetail(): boolean {
		return !!this.detail;
	}
}

@Component({
	selector: 'app-stepper-navigation-header',
	template: ` <div class="navigation-header-container">
			<app-breadcrumb
				data-automation-id="navigation-header-breadcrumb"
			></app-breadcrumb>
		</div>
		<ng-content></ng-content>`,
	styleUrls: ['app-navigation-header.component.scss'],
	imports: [forwardRef(() => AppBreadcrumbComponent)],
})
export class AppStepperNavigationHeaderComponent
	implements AfterViewInit, OnChanges
{
	@Input() title = '';
	@Input() detail?: string;
	@Input() svgName = '';

	@ContentChildren(MatStepper, { descendants: true, read: ElementRef })
	contentChildren!: QueryList<ElementRef>;

	svgIcon!: SVGElement;

	constructor(
		private readonly renderer: Renderer2,
		private readonly iconRegistry: MatIconRegistry
	) {}

	ngAfterViewInit(): void {
		this.updateTabHeader();
		if (this.svgName !== undefined && this.svgName.length > 0) {
			this.iconRegistry.getNamedSvgIcon(this.svgName).subscribe((res) => {
				this.svgIcon = res;
				this.updateTabHeader();
			});
		}
	}

	ngOnChanges(): void {
		this.updateTabHeader();
	}

	private updateTabHeader(): void {
		const matStepperContainer =
			this.contentChildren?.first?.nativeElement?.querySelector(
				'.mat-horizontal-stepper-header-container'
			);
		if (matStepperContainer) {
			let appStepperContainer = matStepperContainer.closest(
				'.navigation-header-stepper-container'
			);
			if (!appStepperContainer) {
				appStepperContainer = wrapElementWith(
					matStepperContainer,
					'div',
					['navigation-header-stepper-container'],
					this.renderer
				);
			}

			appStepperContainer
				.querySelector('.navigation-header-title-container')
				?.remove();

			const detailHtml = !!this.detail
				? `&nbsp;<span class="navigation-header-detail mat-h1">|&nbsp;${this.detail}</span>`
				: '';
			const titleHtml = `<div class="navigation-header-title-container"><span class="navigation-header-title mat-h1">${this.title}</span>${detailHtml}</div>`;
			appStepperContainer.insertAdjacentHTML('afterbegin', titleHtml);

			if (this.svgIcon !== undefined) {
				appStepperContainer.insertAdjacentHTML(
					'afterbegin',
					`<div class="navigation-header-stepper-icon">${this.svgIcon.outerHTML}</div>`
				);
			}
		}
	}
}

@Component({
	selector: 'app-breadcrumb',
	template: ` @if (showBreadcrumbs) {
		<div class="breadcrumb">
			@for (item of treeItems; track breadcrumbKey(item)) {
				<div class="breadcrumb-tree-item">
					<a
						[routerLink]="item.routerLink"
						[queryParams]="item.queryParams"
						[attr.data-automation-id]="
							'navigation-header-' + item.label + '-link'
						"
						>{{ item.label }}</a
					>
					<div class="breadcrumb-tree-spacer">/</div>
				</div>
			}
			@for (item of targetItem; track breadcrumbKey(item)) {
				<div class="breadcrumb-target-item">
					{{ item.label }}
				</div>
			}
		</div>
	}`,
	styleUrls: ['app-navigation-header.component.scss'],
	imports: [RouterLink],
})
export class AppBreadcrumbComponent implements OnInit, OnDestroy {
	breadcrumbs: Breadcrumb[] = [];
	treeItems: Breadcrumb[] = [];
	targetItem: Breadcrumb[] = [];

	navigationSubscription?: Subscription;

	get showBreadcrumbs(): boolean {
		return this.breadcrumbs.length > 0;
	}

	constructor(public readonly navHistory: NavigationHistoryService) {}

	ngOnInit(): void {
		this.updateBreadcrumbs();
		this.navigationSubscription = this.navHistory.changesOccurred.subscribe(
			(changedRoute) => {
				this.updateBreadcrumbs();
			}
		);
	}

	updateBreadcrumbs(breadcrumbCount: number = 99): void {
		this.breadcrumbs = this.navHistory
			.getLastRoutes(breadcrumbCount)
			.map((route) => {
				let url: any;
				if (route.url === `${route.url}`) {
					url = [route.url as string];
				} else {
					url = route.url;
				}

				if (!url) {
					url = [];
				}

				const label = route.name ?? '';
				let routerLink = `/${url.join('/')}`.replace(/\/\//g, '/');

				const queryParams = { ...route.queryParams };
				queryParams[BREADCRUMB_UNIQUE_QUERYPARAM] = Math.floor(
					Math.random() * 999999 + 100000
				);

				// If URL has queryparams, move them to the queryParams object
				if (routerLink.indexOf('?') >= 0) {
					const urlParts = routerLink.split('?');
					routerLink = urlParts[0];
					const qs = urlParts[1];

					const kvps = qs.split('&');
					kvps.forEach((pair) => {
						const kvpParts = pair.split('=');
						queryParams[kvpParts[0]] = kvpParts[1];
					});
				}

				return { label, routerLink, queryParams };
			});
		this.treeItems =
			this.breadcrumbs.length > 0 ? this.breadcrumbs.slice(0, -1) : [];
		this.targetItem = this.breadcrumbs.slice(-1);
	}

	breadcrumbKey(item: Breadcrumb): string {
		return `${item.label}|${item.routerLink}`;
	}

	ngOnDestroy(): void {
		this.navigationSubscription?.unsubscribe();
		this.navigationSubscription = undefined;
	}
}

export interface Breadcrumb {
	label: string;
	routerLink: string | string[] | UrlSegment[];
	queryParams: any;
}

const wrapElementWith = (
	element: any,
	tag: string,
	classes: string[],
	renderer: Renderer2
): any => {
	const wrapper = renderer.createElement(tag);
	classes.forEach((it) => wrapper.classList.add(it));

	element.parentNode.insertBefore(wrapper, element);
	wrapper.appendChild(element);

	return wrapper;
};
